import { SetStateAction, useEffect, useLayoutEffect, useRef, useState } from "react";
import AvatarEditor from "react-avatar-editor";
import { Span } from "../../../Text/Text";
import Button, { ButtonModeE } from "../../../Button/Button";
import styles from "./styles.module.scss";

type ImageEditorPropsT = {
  file: string | File;
  onSave: (file: any) => void;
  setShowEditor: (value: SetStateAction<boolean>) => void;
  /** Called when user cancels (closes without saving). Use to reset upload state. */
  onCancel?: () => void;
  /** When true, show "Keep original ratio" checkbox and allow rectangular crop. */
  allowKeepOriginalRatio?: boolean;
};

const IMG_EDGE_LENGTH = 200;
const SCALE = {
  MIN: 1,
  MAX: 2,
  STEP_SIZE: 0.1,
};
const ROTATION = {
  MIN: 0,
  MAX: 360,
  STEP_SIZE: 15,
};
const EDITOR_BG_COLOR = [0, 0, 0, 0.6];
const EDITOR_BORDER_WIDTH = 50;

function getEditorSize(
  keepOriginalRatio: boolean,
  imageDimensions: { width: number; height: number } | null
): { width: number; height: number } {
  if (!keepOriginalRatio || !imageDimensions) {
    return { width: IMG_EDGE_LENGTH, height: IMG_EDGE_LENGTH };
  }
  const { width: w, height: h } = imageDimensions;
  const max = IMG_EDGE_LENGTH;
  if (w >= h) {
    return { width: max, height: Math.round((max * h) / w) };
  }
  return { width: Math.round((max * w) / h), height: max };
}

const ImageEditor = ({
  file,
  onSave,
  setShowEditor,
  onCancel,
  allowKeepOriginalRatio = false,
}: ImageEditorPropsT) => {
  const [scale, setScale] = useState(SCALE.MIN);
  const [rotation, setRotation] = useState(ROTATION.MIN);
  const [keepOriginalRatio, setKeepOriginalRatio] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const cropRef = useRef(null);

  const imageSrc = typeof file === "string" ? file : objectUrl;

  useLayoutEffect(() => {
    if (typeof file !== "string" && file) {
      const url = URL.createObjectURL(file);
      setObjectUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        setObjectUrl(null);
      };
    }
    setObjectUrl(null);
  }, [file]);

  useEffect(() => {
    if (!allowKeepOriginalRatio || !imageSrc) {
      setImageDimensions(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = imageSrc;
  }, [allowKeepOriginalRatio, imageSrc]);

  const { width: editorWidth, height: editorHeight } = getEditorSize(
    keepOriginalRatio,
    imageDimensions
  );

  const handleChangeScale = (e) => {
    setScale(+e.target.value);
  };
  const handleChangeRotation = (e) => {
    setRotation(+e.target.value);
  };

  const handleSave = async () => {
    if (cropRef?.current) {
      const image = cropRef.current.getImage();
      onSave(image.toDataURL());
    }
    closeEditor();
  };

  const closeEditor = () => {
    setShowEditor(false);
  };

  const handleCancel = () => {
    onCancel?.();
    closeEditor();
  };

  if (!imageSrc) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.wrapper}>
        <AvatarEditor
          ref={cropRef}
          image={imageSrc}
          width={editorWidth}
          height={editorHeight}
          border={EDITOR_BORDER_WIDTH}
          color={EDITOR_BG_COLOR}
          scale={scale}
          rotate={rotation}
        />
        <div className={styles.controls}>
          {allowKeepOriginalRatio && (
            <div className={styles.keepRatioRow}>
              <input
                type="checkbox"
                id="keepOriginalRatio"
                checked={keepOriginalRatio}
                onChange={(e) => setKeepOriginalRatio(e.target.checked)}
              />
              <label htmlFor="keepOriginalRatio">
                <Span>Keep original ratio</Span>
              </label>
            </div>
          )}
          <span>
            <Span>Scale</Span>:
          </span>
          <input
            type="range"
            min={SCALE.MIN}
            max={SCALE.MAX}
            step={SCALE.STEP_SIZE}
            value={scale}
            onChange={handleChangeScale}
          />
          <span>
            <Span>Rotation</Span>:
          </span>
          <input
            type="range"
            min={ROTATION.MIN}
            max={ROTATION.MAX}
            step={ROTATION.STEP_SIZE}
            value={rotation}
            onChange={handleChangeRotation}
          />
          <div className={styles.buttons}>
            <Button onClick={handleCancel} mode={ButtonModeE.OUTLINED}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;
